import { logger } from '../utils';
import ApiError from './ApiError';
import { ENV } from '@bombom/shared/env';

const DEFAULT_ERROR_MESSAGES: Record<number, string> = {
  400: '잘못된 요청입니다. 입력값을 확인해주세요.',
  401: '로그인이 필요합니다.',
  403: '접근 권한이 없습니다.',
  404: '요청한 리소스를 찾을 수 없습니다.',
  413: '파일 용량이 너무 큽니다. 더 작은 파일로 시도해주세요.',
  500: '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.',
};

type JsonBody = Record<string, unknown> | unknown[] | FormData;
type Query = Record<string, string | number | boolean | undefined | string[]>;

type FetcherOptions<TRequest extends JsonBody> = {
  path: string;
  baseUrl?: string;
  query?: Query;
  body?: TRequest;
  headers?: Record<string, string>;
};

type GetFetcherOptions = FetcherOptions<never> & {
  credentials?: RequestCredentials;
  signal?: AbortSignal;
};

export const fetcher = {
  get: async <TResponse>({
    path,
    baseUrl,
    query,
    credentials,
    headers,
    signal,
  }: GetFetcherOptions) =>
    request<never, TResponse>({
      path,
      baseUrl,
      query,
      credentials,
      headers,
      method: 'GET',
      signal,
    }),
  post: async <TRequest extends JsonBody, TResponse>({
    path,
    baseUrl,
    body,
    headers,
  }: FetcherOptions<TRequest>) =>
    request<TRequest, TResponse>({
      path,
      baseUrl,
      body,
      method: 'POST',
      headers,
    }),
  patch: async <TRequest extends JsonBody, TResponse>({
    path,
    baseUrl,
    query,
    body,
    headers,
  }: FetcherOptions<TRequest>) =>
    request<TRequest, TResponse>({
      path,
      baseUrl,
      query,
      body,
      method: 'PATCH',
      headers,
    }),
  put: async <TRequest extends JsonBody, TResponse>({
    path,
    baseUrl,
    body,
    headers,
  }: FetcherOptions<TRequest>) =>
    request<TRequest, TResponse>({
      path,
      baseUrl,
      body,
      method: 'PUT',
      headers,
    }),
  delete: async <TRequest extends JsonBody, TResponse>({
    path,
    baseUrl,
    body,
    headers,
  }: FetcherOptions<TRequest>) =>
    request<TRequest, TResponse>({
      path,
      baseUrl,
      body,
      method: 'DELETE',
      headers,
    }),
};

type FetchMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';

type RequestOptions<TRequest> = {
  path: string;
  baseUrl?: string;
  method: FetchMethod;
  query?: Query;
  body?: TRequest;
  headers?: Record<string, string>;
  credentials?: RequestCredentials;
  signal?: AbortSignal;
};

const request = async <TRequest, TResponse>({
  path,
  baseUrl = ENV.baseUrl,
  method,
  query = {},
  body,
  headers,
  credentials = 'include',
  signal,
}: RequestOptions<TRequest>): Promise<TResponse> => {
  try {
    const url = new URL(baseUrl + path);
    const stringifiedQuery: Record<string, string> = Object.fromEntries(
      Object.entries(query)
        .map(([key, value]) => [key, value?.toString()])
        .filter(([, value]) => value),
    );
    url.search = new URLSearchParams(stringifiedQuery).toString();

    const isFormData = body instanceof FormData;

    const config: RequestInit = {
      method,
      credentials,
      signal,
      headers: {
        ...(!isFormData && { 'Content-Type': 'application/json' }),
        ...headers,
      },
    };

    if (body && (method === 'POST' || method === 'PATCH' || method === 'PUT')) {
      config.body = isFormData ? body : JSON.stringify(body);
    }

    const response = await fetch(url, config);

    if (!response.ok) {
      const status = response.status;
      const contentType = response.headers.get('Content-Type');
      let rawBody;
      let errorMessage = DEFAULT_ERROR_MESSAGES[status];

      try {
        if (contentType?.includes('application/json')) {
          rawBody = await response.json();
          errorMessage = rawBody.message ?? errorMessage;
        } else {
          rawBody = await response.text();
          errorMessage = rawBody || errorMessage;
        }
      } catch {
        errorMessage = '응답 파싱에 실패했습니다.';
      }

      throw new ApiError(status, errorMessage ?? '', rawBody);
    }

    const contentType = response.headers.get('Content-Type');

    if (contentType?.includes('application/json')) {
      try {
        return await response.json();
      } catch {
        throw new ApiError(response.status, 'JSON 파싱에 실패했습니다.');
      }
    }

    return undefined as TResponse;
  } catch (error) {
    logger.error(error);
    throw error;
  }
};
